import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { FiChevronLeft, FiChevronRight, FiChevronRight as FiBreadcrumb } from 'react-icons/fi'
import { useFetch } from '../hooks/useFetch'
import { productService } from '../services/product.service'
import Layout from '../components/Layout/Layout'
import ProductCarousel from '../components/Carousel/ProductCarousel'
import Button from '../components/UI/Button'
import Loader from '../components/UI/Loader'
import { formatPrice } from '../utils/formatters'
import { handleBuyInApp } from '../utils/helpers'

const ProductDetail = () => {
  const { productId } = useParams()
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [selectedColor, setSelectedColor] = useState('')
  const [selectedMaterial, setSelectedMaterial] = useState('')
  const [selectedCrystal, setSelectedCrystal] = useState('')
  const [thumbnailHeight, setThumbnailHeight] = useState(0)
  const mainImageRef = useRef(null)
    
  // Fetch product details
  const { 
    data: productResponse, 
    loading: productLoading, 
    error: productError 
  } = useFetch(() => productService.getProductById(productId), [productId])
  
  const product = productResponse?.data
    
  // Fetch related products
  const { 
    data: relatedProducts,
    loading: relatedLoading 
  } = useFetch(
    () => product?.data_category?._id 
      ? productService.getProductsByCategory(product.data_category.category, { limit: 6 })
      : null,
    [product?.data_category?._id]
  )
  const baseUrl =  import.meta.env.VITE_URL_IMAGES_BACK
  // Memoize images to avoid useEffect dependency issues
  // const images = useMemo(() => {
  //   return product?.images?.length > 0 ? product.images : ['/images/no-image.png']
  // }, [product?.images])
  const images = useMemo(() => {
    return (product?.images?.length > 0 ? product.images : ['no-image.png'])
      .map(img => {
        if (!img) return `${baseUrl}no-image.png`
        if (img.startsWith('http')) return img

        return `${baseUrl}/images/products/${img}`
      })
  }, [product?.images])
    
  // Extract unique values from variants
  const availableOptions = useMemo(() => {
    if (!product?.variants) return { colors: [], materials: [], crystals: [] }
    
    const colors = new Set()
    const materials = new Set() 
    const crystals = new Set()
    
    product.variants.forEach(variant => {
      // Colors from variant colors array
      variant.colors?.forEach(colorObj => {
        if (colorObj.color) colors.add(colorObj.color)
      })
      
      // Materials from data_plated_product
      if (variant.data_plated_product?.plated) {
        materials.add(variant.data_plated_product.plated)
      }
      
      // Crystals from data_glass_product
      if (variant.data_glass_product?.color) {
        crystals.add(variant.data_glass_product.color)
      }
    })
    
    return {
      colors: Array.from(colors),
      materials: Array.from(materials),
      crystals: Array.from(crystals)
    }
  }, [product?.variants])
  
  // Set default selections when product loads
  useEffect(() => {
    if (availableOptions.colors.length > 0 && !selectedColor) {
      setSelectedColor(availableOptions.colors[0])
    }
    if (availableOptions.materials.length > 0 && !selectedMaterial) {
      setSelectedMaterial(availableOptions.materials[0])
    }
    if (availableOptions.crystals.length > 0 && !selectedCrystal) {
      setSelectedCrystal(availableOptions.crystals[0])
    }
  }, [availableOptions, selectedColor, selectedMaterial, selectedCrystal])
  
  // Sync thumbnail height with main image
  useEffect(() => {
    const updateHeight = () => {
      if (mainImageRef.current) {
        const height = mainImageRef.current.offsetHeight
        setThumbnailHeight(height)
      }
    }

    updateHeight()
    
    const resizeObserver = new ResizeObserver(updateHeight)
    if (mainImageRef.current) {
      resizeObserver.observe(mainImageRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [images])
    
  const handleImageError = (e) => {
    e.target.src = '/images/no-image.png'
  }
  
  const handlePrevImage = () => {
    if (!product?.images || product.images.length <= 1) return
    setSelectedImageIndex(prev => 
      prev === 0 ? product.images.length - 1 : prev - 1
    )
  }
  
  const handleNextImage = () => {
    if (!product?.images || product.images.length <= 1) return
    setSelectedImageIndex(prev => 
      prev === product.images.length - 1 ? 0 : prev + 1
    )
  }
    
  const handleBuyClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    handleBuyInApp()
  }
    
  // Get price from first variant (assuming all variants have same price)
  const price = product?.variants?.[0]?.public_price || 0
  
  // Get description from first variant
  const description = product?.variants?.[0]?.description || ''
  
  // Filter out current product from related products
  const filteredRelatedProducts = relatedProducts?.data?.products?.filter(
    (relatedProduct) => relatedProduct._id !== product?._id
  ) || []
    
  if (productError) {
    return (
      <Layout>
        <div className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-2xl font-title font-bold text-primary-text mb-4">
                Producto no encontrado
              </h1>
              <p className="text-primary-gray mb-8">
                El producto que buscas no existe o no está disponible.
              </p>
              <Link to="/">
                <Button variant="ghost">
                  Volver al Inicio
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    )
  }
    
  if (productLoading) {
    return (
      <Layout>
        <div className="py-16">
          <Loader size="large" text="Cargando producto..." />
        </div>
      </Layout>
    )
  }
    
  if (!product) {
    return (
      <Layout>
        <div className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-2xl font-title font-bold text-primary-text mb-4">
                Producto no encontrado
              </h1>
              <p className="text-primary-gray mb-8">
                El producto que buscas no existe o no está disponible.
              </p>
              <Link to="/">
                <Button variant="primary">
                  Volver al Inicio
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    )
  }
    
  const hasMultipleImages = images.length > 1
    
  return (
    <Layout>
      <div className="py-4 sm:py-6 lg:py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm sm:text-base text-primary-gray mb-4 sm:mb-6 font-body font-extralight flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-dark hover:font-normal transition-colors">
              Inicio
            </Link>
            <FiBreadcrumb className="mx-1 sm:mx-2 flex-shrink-0" />
            <span className="hover:text-primary-dark hover:font-normal transition-colors">Catálogo</span>
            <FiBreadcrumb className="mx-1 sm:mx-2 flex-shrink-0" />
            {product.data_category && (
              <>
                <Link 
                  to={`/categoria/${product.data_category.category.toLowerCase()}`}
                  className="hover:text-primary-dark hover:font-normal transition-colors"
                >
                  {product.data_category.category}
                </Link>
                <FiBreadcrumb className="mx-1 sm:mx-2 flex-shrink-0" />
              </>
            )}
            <span className="font-normal truncate">
              {product.name}
            </span>
          </nav>
                    
          {/* Product Detail */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12 lg:mb-16">
            {/* Image Section */}
            <div className="w-full">
              <div className="flex flex-col space-y-3 sm:space-y-4 lg:space-y-0 lg:flex-row lg:gap-4">
                {/* Thumbnails */}
                {hasMultipleImages && (
                  <div className="order-2 lg:order-1 w-full lg:w-16 xl:w-20">
                    {/* Mobile: Horizontal scroll */}
                    <div className="flex lg:hidden gap-2 overflow-x-auto pb-2 -mx-1">
                      {images.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImageIndex(index)}
                          className={`aspect-square overflow-hidden rounded border-2 transition-colors duration-200 flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 ${
                            selectedImageIndex === index
                              ? 'border-primary-dark'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <img
                            src={image}
                            alt={`${product.name} ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={handleImageError}
                          />
                        </button>
                      ))}
                    </div>
                    
                    {/* Desktop: Vertical scroll - Synced height */}
                    <div 
                      className="hidden lg:flex flex-col gap-2 overflow-y-auto w-full"
                      style={{ height: thumbnailHeight > 0 ? `${thumbnailHeight}px` : 'auto' }}
                    >
                      {images.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImageIndex(index)}
                          className={`aspect-square overflow-hidden rounded border-2 transition-colors duration-200 flex-shrink-0 w-full ${
                            selectedImageIndex === index
                              ? 'border-primary-dark'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <img
                            src={image}
                            alt={`${product.name} ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={handleImageError}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Main Image - Always first on mobile */}
                <div className="order-1 lg:order-2 w-full lg:flex-1">
                  <div 
                    ref={mainImageRef}
                    className="aspect-square w-full overflow-hidden bg-primary-light rounded-lg relative"
                  >
                    <img
                      src={images[selectedImageIndex]}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={handleImageError}
                    />
                    
                    {/* Navigation arrows */}
                    {hasMultipleImages && (
                      <>
                        <button
                          onClick={handlePrevImage}
                          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-lg rounded-full p-1.5 sm:p-2 transition-colors duration-200"
                        >
                          <FiChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button
                          onClick={handleNextImage}
                          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-lg rounded-full p-1.5 sm:p-2 transition-colors duration-200"
                        >
                          <FiChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
                        
            {/* Product Info */}
            <div className="w-full space-y-4 sm:space-y-6">
              {/* Title */}
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-title font-semibold text-primary-text mb-2">
                  {product.name}
                </h1>
              </div>
              
              {/* Description */}
              <div>
                <p className="text-base font-extralight text-primary-black leading-relaxed">
                  {description}
                </p>
              </div>

              {/* Price */}
              <div className='border-b border-black pb-2 sm:pb-4'>
                <div className="text-3xl sm:text-2xl font-title font-semibold text-primary-black">
                  {formatPrice(price)}
                </div>
              </div>
              
              {/* Options */}
              <div className="space-y-3 sm:space-y-4">
                {/* Materials */}
                {availableOptions.materials.length > 0 && (
                  <div>
                    <label className="block text-base font-normal text-primary-black mb-2">
                      Materiales disponibles
                    </label>
                    <select
                      value={selectedMaterial}
                      onChange={(e) => setSelectedMaterial(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-dark focus:border-transparent"
                    >
                      {availableOptions.materials.map(material => (
                        <option key={material} value={material}>{material}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Crystals */}
                {availableOptions.crystals.length > 0 && (
                  <div>
                    <label className="block text-base font-normal text-primary-black mb-2">
                      Cristales disponibles
                    </label>
                    <select
                      value={selectedCrystal}
                      onChange={(e) => setSelectedCrystal(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-dark focus:border-transparent"
                    >
                      {availableOptions.crystals.map(crystal => (
                        <option key={crystal} value={crystal}>{crystal}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Colors */}
                {availableOptions.colors.length > 0 && (
                  <div>
                    <label className="block text-base font-normal text-primary-black mb-2">
                      Colores disponibles
                    </label>
                    <select
                      value={selectedColor}
                      onChange={(e) => setSelectedColor(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-dark focus:border-transparent"
                    >
                      {availableOptions.colors.map(color => (
                        <option key={color} value={color}>{color}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
                            
              {/* Buy Button */}
              <div className="pt-2 sm:pt-4 lg:pt-6">
                <Button
                  onClick={handleBuyClick}
                  variant="primary"
                  size="large"
                  className="w-full font-semibold text-lg sm:text-base py-3 sm:py-4"
                >
                  COMPRAR EN LA APLICACIÓN
                </Button>
              </div>
            </div>
          </div>
                    
          {/* Related Products */}
          <div className="w-full overflow-hidden">
            {filteredRelatedProducts.length > 0 ? (
              <ProductCarousel
                products={filteredRelatedProducts}
                title="Productos relacionados"
                showExploreButton={true}
                className="w-full"
              />
            ) : (
              relatedLoading ? (
                <div>
                  <h2 className="text-2xl sm:text-3xl font-title font-semibold text-primary-text mb-8 text-center">
                    Productos Relacionados
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <div key={index} className="animate-pulse">
                        <div className="aspect-square bg-gray-200 rounded-lg mb-3"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default ProductDetail